import { PlainButton } from "@jugl-web/ui-components";
import { useTheme } from "@web-src/themes/useTheme";

import { cx, useAppVariant, useTranslations } from "@jugl-web/utils";
import { FC, useCallback, useEffect, useState } from "react";
import { logger } from "@web-src/utils/logger";
import { ReactComponent as CrossIcon } from "./assets/cross-icon.svg";
import { ReactComponent as LampIcon } from "./assets/lamp-icon.svg";

export const NotificationsPermissionsPopup: FC = () => {
  const { customTheme } = useTheme();
  const { t } = useTranslations();
  const { isCx } = useAppVariant();
  const [isVisible, setIsVisible] = useState(false);
  const [notificationStatus, setNotificationStatus] = useState<
    NotificationPermission | "not-supported"
  >(() => {
    if ("Notification" in window) {
      return Notification.permission;
    }
    return "not-supported";
  });

  // Initialize notification permission listeners
  useEffect(() => {
    navigator.permissions
      ?.query({ name: "notifications" })
      .then((permissionStatus) => {
        permissionStatus.onchange = () => {
          setNotificationStatus(Notification.permission);
        };
      })
      .catch((error) => {
        logger.error("Error querying notification permissions:", error);
      });
  }, []);

  // Check if the notification popup was closed within the last 24 hours
  const wasClosedWithin24Hours = useCallback(() => {
    const lastClosedTimestamp = localStorage.getItem(
      "notificationPopupWasClosed"
    );
    if (!lastClosedTimestamp) return false;

    const lastClosed = new Date(Number(lastClosedTimestamp));
    const now = new Date();
    const hoursDiff = (now.getTime() - lastClosed.getTime()) / (1000 * 60 * 60);

    return hoursDiff < 24;
  }, []);

  useEffect(() => {
    if (
      (notificationStatus === "default" || notificationStatus === "denied") &&
      !wasClosedWithin24Hours()
    ) {
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
  }, [notificationStatus, wasClosedWithin24Hours]);

  const handleClose = useCallback(() => {
    localStorage.setItem("notificationPopupWasClosed", Date.now().toString());
    setIsVisible(false);
  }, []);

  if (!isVisible || isCx) {
    return null;
  }

  return (
    <div
      className="fixed bottom-0 left-[50%] z-[99] -translate-x-1/2 transform pb-10 transition"
      onMouseDown={(e) => e.stopPropagation()}
      role="dialog"
    >
      <div
        className={cx(
          "util-prevent-drawer-close font-secondary relative mx-auto inline-flex w-full items-center justify-center gap-6 rounded-2xl py-3 pl-4 pr-10 text-sm font-medium text-white md:px-[120px]",
          {
            "bg-primary-900": customTheme === "blue",
            "bg-primary-800": customTheme === "light",
            "bg-[#333333]": customTheme === "dark",
          }
        )}
      >
        <div className="flex items-center gap-2">
          <LampIcon />
          <span className="md:whitespace-nowrap">
            {t({
              id: "notifications.enable-notifications",
              defaultMessage:
                "Enable Notifications for the Jugl.com in your browser",
            })}
          </span>
        </div>
        {notificationStatus !== "denied" && (
          <PlainButton
            className="font-secondary whitespace-nowrap rounded-lg bg-white bg-opacity-20 px-6 py-4 text-sm font-medium text-white transition-colors hover:bg-opacity-30"
            onClick={() => {
              Notification.requestPermission();
              handleClose();
            }}
          >
            {t({
              id: "notifications.open-settings",
              defaultMessage: "Open Settings",
            })}
          </PlainButton>
        )}
        <PlainButton
          className="absolute right-6 top-[50%] -translate-y-1/2 transform"
          onClick={(e) => {
            handleClose();
            e.stopPropagation();
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <CrossIcon />
        </PlainButton>
      </div>
    </div>
  );
};
